/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import SqlImportModel, { SyncTarget } from 'cio/models/sql-import';
import integerToString from 'cio/utils/integer-to-string';
import timeframeUnits from 'cio/utils/timeframe-units';
import timeframePropertyComputed from 'cio/utils/timeframe-computed';
import EnvironmentService from 'cio/services/environment';
import SegmentModel from 'cio/models/segment';
import FormValidations from 'cio/utils/form-validations';
import { ImportType } from 'cio/utils/sql-import';
import { ONE_HOUR_IN_SECONDS } from 'cio/utils/time-constants';
import AnalyticsService from 'cio/services/analytics';
import MomentService from 'cio/services/moment';
import moment from 'moment-timezone';
import ShortSegmentModel from 'cio/models/short-segment';
import DS from 'ember-data';
import { task } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';

interface SqlImportsCreateSettingsArgs {
  model: SqlImportModel;
  segments: ShortSegmentModel[];
  onInsert?: (context: SqlImportsCreateSettings) => unknown;
  canCreateSegment?: boolean;
  onSubmit: () => unknown;
  onCancel: () => unknown;
  readOnly: boolean;
  resetIntervalCounts: boolean;
}

const Validations = {
  startAt(this: SqlImportsCreateSettings, timestamp: number) {
    // skip validations if the input is hidden (for already started sync):
    if (this.wasScheduledAndAlreadyStarted) {
      return true;
    }

    if (timestamp > 0 && timestamp <= this.moment.currentTimestamp) {
      return 'The scheduled time needs to be in the future';
    }

    return true;
  },

  idFieldName(this: SqlImportsCreateSettings, value?: string) {
    if (this.syncTarget !== SyncTarget.People || (value && value.trim() !== '')) {
      return true;
    }

    return "Attribute can't be blank";
  },

  segment(this: SqlImportsCreateSettings, value: SegmentModel) {
    // If not importing to segment or not importing to existing segment, an empty value is valid.
    if (!this.args.model.isImportingToSegment || this.segmentType !== SegmentType.Existing) {
      return true;
    }

    if (!value.get('id')) {
      return "Segment can't be blank";
    }

    return true;
  },

  segmentName(this: SqlImportsCreateSettings, value?: string) {
    // If not importing to segment or not importing to new segment, an empty value is valid.
    if (!this.args.model.isImportingToSegment || this.segmentType !== SegmentType.New) {
      return true;
    }

    if (!value || value.trim() === '') {
      return "Segment name can't be blank";
    }

    return true;
  },
};

enum SegmentType {
  Existing = 'existing',
  New = 'new',
}

export default class SqlImportsCreateSettings extends Component<SqlImportsCreateSettingsArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service moment!: MomentService;
  @service store!: DS.Store;

  readonly SyncTargetValues = SyncTarget;
  readonly SegmentType = SegmentType;

  readonly validations = FormValidations.create({
    context: this,
    validations: Validations,
    modelKey: 'args.model',
  });

  readonly updatePeopleOptions = [
    { label: 'By id', value: 'id' },
    { label: 'By email', value: 'email' },
  ];

  @tracked startAtMoment: moment.Moment | null = null;
  @tracked wasScheduledAndAlreadyStarted = false;
  @tracked syncTarget = this.args.model.belongsTo('objectType').id() ?? this.SyncTargetValues.People;

  constructor(owner: unknown, args: SqlImportsCreateSettingsArgs) {
    super(owner, args);

    this.startAtMoment = args.model.startAt !== 0 ? moment.unix(args.model.startAt) : null;
    this.wasScheduledAndAlreadyStarted =
      !args.model.isDraft && args.model.startAt > 0 && args.model.startAt < this.moment.currentTimestamp;
  }

  @timeframePropertyComputed({
    countProperty: 'intervalCountValue',
    unitProperty: 'intervalUnit',
    defaultUnitWhenZero: 'minute',
  })
  interval!: number;

  readonly intervalUnits = timeframeUnits({
    context: this,
    countProperty: 'intervalCountValue',
  });

  @tracked intervalUnit = 'minute';
  @tracked intervalCountValue = 1;
  @tracked segmentType: SegmentType = this.canCreateSegment ? SegmentType.New : SegmentType.Existing;

  @integerToString('intervalCountValue') inputIntervalCountValue!: string;

  get objectTypes() {
    return this.store.peekAll('object-type').toArray();
  }

  get hasNoSegments() {
    return !this.args.segments || this.args.segments.length === 0;
  }

  get canCreateSegment() {
    return this.args.canCreateSegment ?? this.auth.hasPermission('journeys/segments/create');
  }

  get canChooseSyncTarget() {
    return this.args.model.isNew || this.args.model.isDraft;
  }

  @action
  onInsert() {
    let { model } = this.args;

    if (model.interval) {
      this.interval = model.interval;
    } else {
      this.interval = ONE_HOUR_IN_SECONDS;
      this.didChangeInterval();
    }

    this.args.onInsert?.(this);
  }

  @action
  async onSubmit(ev: MouseEvent | KeyboardEvent) {
    ev.preventDefault();
    try {
      await this.validations.validateModel();
      this.args.onSubmit();
    } catch {}
    let data = {
      interval: this.intervalCountValue,
      frequency: this.intervalUnit,
      syncToSegment: this.args.model.importType === ImportType.WorkspaceAndSegment,
      syncTarget:
        this.args.model.syncTarget === this.SyncTargetValues.People
          ? this.SyncTargetValues.People
          : this.SyncTargetValues.Objects,
    };
    this.analytics.track('sql_imports', 'select_database_started', data);
  }

  @action
  didChangeInterval(field?: 'intervalUnit' | 'intervalCountValue', value?: number | string) {
    if (field != null && value != null) {
      set(this, field as any, value);
    }

    this.args.model.set('interval', this.interval);
  }

  @action
  didChangeIdField(option: Record<string, any>) {
    this.args.model.set('idFieldName', option.value);
    this.validations.validateAttribute('idFieldName');
  }

  @action
  didChangeSegmentName(segmentName: string) {
    this.args.model.set('segmentName', segmentName);
    this.validations.validateAttribute('segmentName');
  }

  @action
  didToggleImportToSegment() {
    let { model } = this.args;

    if (model.importType === ImportType.WorkspaceAndSegment) {
      model.set('importType', ImportType.Workspace);

      model.set('segment', null);
      // Validate these so any errors can be cleared out.
      this.validations.validateAttribute('segment');
      this.validations.validateAttribute('segmentName');
    } else {
      model.set('importType', ImportType.WorkspaceAndSegment);
    }
  }

  didSelectSegment = task({ restartable: true }, async (shortSegment: ShortSegmentModel) => {
    let realSegment =
      this.store.peekRecord('segment', shortSegment.id) ?? (await this.store.findRecord('segment', shortSegment.id));
    this.args.model.set('segment', realSegment);
    this.validations.validateAttribute('segment');
  });

  @action
  setStartAt(val?: { date: moment.Moment }) {
    if (val?.date == null) {
      this.startAtMoment = null;
      this.args.model.set('startAt', 0);
    } else {
      this.startAtMoment = val.date;
      this.args.model.set('startAt', this.startAtMoment.unix());
    }

    this.validations.validateAttribute('startAt');
  }

  @action
  convertStartAtMomentToLocalTimezone() {
    if (this.startAtMoment != null) {
      this.startAtMoment = moment.unix(this.startAtMoment.unix());
    }
  }

  @action
  onUpdate() {
    if (this.args.resetIntervalCounts) {
      let { model } = this.args;

      if (model.interval) {
        this.interval = model.interval;
      } else {
        this.interval = ONE_HOUR_IN_SECONDS;
        this.didChangeInterval();
      }
    }
  }

  @action
  onToggleSyncType(syncTarget: SyncTarget.People | string) {
    this.syncTarget = syncTarget;
    if (syncTarget !== this.SyncTargetValues.People) {
      this.validations.validateModel();

      this.args.model.setProperties({
        idFieldName: ObjectIdentifierKey.ObjectId,
        importType: ImportType.Object,
      });

      this.args.model.objectType = this.store.peekRecord('objectType', Number(syncTarget));
    } else {
      this.validations.validateModel();

      this.args.model.setProperties({
        idFieldName: null,
        importType: ImportType.Workspace,
      });
      this.args.model.objectType = null as any;
    }
  }
}
